import {
  add,
  InvalidMoneyError,
  money,
  parseMoney,
  subtract,
  toDecimalString,
  type Money,
} from "@stb/core/money"
import {
  INCOME_TAX_YEARS,
  incomeTaxYear,
  takeHomePay,
  type Bracket,
  type IncomeTaxYear,
  type PayPeriod,
  type PeriodBreakdown,
  type Residency,
  type ShownPeriod,
  type StudyLoanBand,
  type TakeHomeResult,
} from "@stb/jurisdiction-au/income-tax"

import type { Locale } from "@/core/i18n/locale"

import { MODEL_COPY_ZH } from "../../content/zh/pay-calculator-copy"

/** Sentences the model builds (errors, the prerendered answers, the bracket caption, HELP band wording) per language. */
export interface ModelCopy {
  outOfRange: string
  empty: string
  amount: string
  other: string
  hours: string
  days: string
  answer: (v: {
    salary: string
    fortnight: string
    fy: string
    tax: string
    levy: string
  }) => string
  helpAnswer: (v: { salary: string; annual: string; fortnight: string; fy: string }) => string
  medicareAnswer: (v: {
    salary: string
    annual: string
    fortnight: string
    fy: string
    rate: number
    taxable: string
  }) => string
  /** "On a taxable income of A$x you pay no income tax on each extra dollar." */
  bracketNil: (taxable: string) => string
  /** `cents` is the marginal rate in cents per dollar, e.g. 30. */
  bracketRate: (taxable: string, cents: number) => string
  bracketTop: string
  bracketNext: (v: { rate: number; from: string; toNext: string }) => string
  rangeOpen: (from: string) => string
  rangeBetween: (from: string, to: string) => string
  ruleNil: string
  /** `cents` is the rate in cents per dollar, e.g. 15. */
  ruleWhole: (cents: number) => string
  ruleOver: (v: { base: string | undefined; cents: number; from: string }) => string
}

const MODEL_COPY_EN: ModelCopy = {
  outOfRange: "That pay is out of range. Try a smaller amount.",
  empty: "Enter your pay before tax to see your take-home pay.",
  amount: "Use an amount between 0 and 10,000,000, such as 85,000 or 32.50.",
  other: "Use an amount between 0 and 10,000,000 for your other job, or leave it at 0.",
  hours: "Use hours a week from 1 to 100, such as 38.",
  days: "Use days a week from 1 to 7, such as 5.",
  answer: (v) =>
    `On a ${v.salary} salary you take home about ${v.fortnight} a fortnight in ${v.fy}, after ${v.tax} income tax and ${v.levy} Medicare levy.`,
  helpAnswer: (v) =>
    `On a ${v.salary} salary with a HELP debt your compulsory repayment is about ${v.annual} a year (${v.fortnight} a fortnight) in ${v.fy}.`,
  medicareAnswer: (v) =>
    `On a ${v.salary} salary the Medicare levy is about ${v.annual} a year (${v.fortnight} a fortnight) in ${v.fy}, ${v.rate}% of ${v.taxable} taxable income.`,
  bracketNil: (t) => `On a taxable income of ${t} you pay no income tax on each extra dollar.`,
  bracketRate: (t, c) => `On a taxable income of ${t} you pay ${c}c on each extra dollar.`,
  bracketTop: " This is the top rate.",
  bracketNext: (v) =>
    ` The ${v.rate}% rate starts at ${v.from}, another ${v.toNext} of taxable income.`,
  rangeOpen: (from) => `${from} and over`,
  rangeBetween: (from, to) => `${from} – ${to}`,
  ruleNil: "Nil",
  ruleWhole: (c) => `${c}c for each A$1 of your whole repayment income`,
  ruleOver: (v) => `${v.base ? `${v.base} plus ` : ""}${v.cents}c for each A$1 over ${v.from}`,
}

const modelCopy = (locale: Locale): ModelCopy => (locale === "zh" ? MODEL_COPY_ZH : MODEL_COPY_EN)

/** Form state of the pay calculator. Text fields stay strings so typing is never fought. */
export interface PayForm {
  amount: string
  period: PayPeriod
  superIncluded: boolean
  fy: string
  residency: Residency
  help: boolean
  other: string
  standardDeduction: boolean
  /** Include the Medicare levy; off for people the ATO exempts. */
  medicare: boolean
  hoursPerWeek: string
  daysPerWeek: string
  show: ShownPeriod
}

export const PAY_PERIODS: readonly PayPeriod[] = [
  "year",
  "month",
  "fortnight",
  "week",
  "day",
  "hour",
]
export const SHOWN_PERIODS: readonly ShownPeriod[] = ["week", "fortnight", "month", "year"]
const RESIDENCY_PARAM: Record<Residency, string> = {
  resident: "resident",
  foreign: "foreign",
  "working-holiday": "whm",
}
export const MAX_AMOUNT = 10_000_000

export const DEFAULT_FY = INCOME_TAX_YEARS.at(-1)?.financialYear ?? ""

export const DEFAULT_FORM: PayForm = {
  amount: "85000",
  period: "year",
  superIncluded: false,
  fy: DEFAULT_FY,
  residency: "resident",
  help: false,
  other: "0",
  // Off by default (Owner 2026-11-01): it depends on the user's own work expenses and only covers wage income.
  standardDeduction: false,
  medicare: true,
  hoursPerWeek: "38",
  daysPerWeek: "5",
  show: "fortnight",
}

/** Which page the calculator sits on; only the defaults and the emphasised result differ. */
export type PayVariant = "pay" | "help" | "medicare"

export const VARIANT_DEFAULTS: Record<PayVariant, PayForm> = {
  pay: DEFAULT_FORM,
  help: { ...DEFAULT_FORM, help: true, show: "year" },
  medicare: { ...DEFAULT_FORM, show: "year" },
}

const stripCurrency = (text: string) => text.trim().replace(/^(?:AU?\$|\$)\s*/i, "")

/**
 * Typed or pasted amount to the canonical form kept in state and the URL ("A$ 85,000" -> "85000", "32.5" stays).
 * Text that is not a number is kept as typed, so the field shows the error instead of silently dropping letters.
 */
export function normalizeAmountInput(text: string): string {
  const bare = stripCurrency(text).replace(/[\s,]/g, "")
  return /^\d*\.?\d*$/.test(bare) ? bare : text
}

/** Canonical amount with thousands separators for display ("85000" -> "85,000"); anything else is returned as is. */
export function groupAmount(canonical: string): string {
  const m = /^(\d*)(\.\d*)?$/.exec(canonical)
  if (!m) return canonical
  const int = (m[1] ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return `${int}${m[2] ?? ""}`
}

/** Index in `display` just after the `n`-th digit or point, so the caret stays put when separators move. */
export function caretAfter(display: string, n: number): number {
  if (n <= 0) return 0
  let seen = 0
  for (let k = 0; k < display.length; k++) {
    if (/[\d.]/.test(display[k]!)) seen++
    if (seen === n) return k + 1
  }
  return display.length
}

/** Whole or decimal dollars, commas allowed, no sign or symbol; undefined when not a valid amount in range. */
export function parseAmount(text: string): Money | undefined {
  try {
    const value = parseMoney(stripCurrency(text).replaceAll(" ", ""), "AUD")
    return value.amount >= 0 && value.amount <= MAX_AMOUNT * 100 ? value : undefined
  } catch (error) {
    if (error instanceof InvalidMoneyError) return undefined
    throw error
  }
}

const oneOf = <T extends string>(allowed: readonly T[], value: string | null, fallback: T): T =>
  allowed.find((a) => a === value) ?? fallback

const amountParam = (value: string | null, fallback: string) =>
  value !== null && parseAmount(value) ? value : fallback

/** 1 to 100 with at most one decimal place (the engine's limit). */
export function parseHours(text: string): number | undefined {
  if (!/^\d{1,3}(\.\d)?$/.test(text.trim())) return undefined
  const n = Number(text)
  return n >= 1 && n <= 100 ? n : undefined
}

export function parseDays(text: string): number | undefined {
  if (!/^[1-7]$/.test(text.trim())) return undefined
  return Number(text)
}

const hoursParam = (value: string | null, fallback: string) =>
  value !== null && parseHours(value) !== undefined ? value : fallback
const daysParam = (value: string | null, fallback: string) =>
  value !== null && parseDays(value) !== undefined ? value : fallback

/** Whitelist parse: unknown, out of range or missing values fall back to the default; never throws. */
export function parseForm(params: URLSearchParams, d: PayForm = DEFAULT_FORM): PayForm {
  const res = params.get("res")
  return {
    amount: normalizeAmountInput(amountParam(params.get("amount"), d.amount)),
    period: oneOf(PAY_PERIODS, params.get("period"), d.period),
    superIncluded: params.get("super") === "incl",
    fy: oneOf(
      INCOME_TAX_YEARS.map((y) => y.financialYear),
      params.get("fy"),
      d.fy,
    ),
    residency:
      (Object.keys(RESIDENCY_PARAM) as Residency[]).find((r) => RESIDENCY_PARAM[r] === res) ??
      d.residency,
    help: params.get("help") === null ? d.help : params.get("help") === "1",
    other: amountParam(params.get("other"), d.other),
    standardDeduction: params.get("sd") === null ? d.standardDeduction : params.get("sd") === "1",
    medicare: params.get("med") === null ? d.medicare : params.get("med") === "1",
    hoursPerWeek: hoursParam(params.get("hpw"), d.hoursPerWeek),
    daysPerWeek: daysParam(params.get("dpw"), d.daysPerWeek),
    show: oneOf(SHOWN_PERIODS, params.get("show"), d.show),
  }
}

/** Only values that differ from the default are written, so the shared link stays short. */
export function formToParams(form: PayForm, d: PayForm = DEFAULT_FORM): URLSearchParams {
  const p = new URLSearchParams()
  if (form.amount !== d.amount && parseAmount(form.amount)) p.set("amount", form.amount.trim())
  if (form.period !== d.period) p.set("period", form.period)
  if (form.superIncluded) p.set("super", "incl")
  if (form.fy !== d.fy) p.set("fy", form.fy)
  if (form.residency !== d.residency) p.set("res", RESIDENCY_PARAM[form.residency])
  if (form.help !== d.help) p.set("help", form.help ? "1" : "0")
  if (form.other !== d.other && parseAmount(form.other)) p.set("other", form.other.trim())
  if (form.standardDeduction !== d.standardDeduction)
    p.set("sd", form.standardDeduction ? "1" : "0")
  if (form.medicare !== d.medicare) p.set("med", form.medicare ? "1" : "0")
  if (form.hoursPerWeek !== d.hoursPerWeek && parseHours(form.hoursPerWeek) !== undefined)
    p.set("hpw", form.hoursPerWeek.trim())
  if (form.daysPerWeek !== d.daysPerWeek && parseDays(form.daysPerWeek) !== undefined)
    p.set("dpw", form.daysPerWeek.trim())
  if (form.show !== d.show) p.set("show", form.show)
  return p
}

export type Outcome =
  | { kind: "ok"; result: TakeHomeResult }
  | { kind: "error"; field: "amount" | "other" | "hours" | "days"; message: string }

/** Validates the form at the boundary and runs the engine; all money is integer cents. */
export function evaluate(form: PayForm, locale: Locale = "en"): Outcome {
  const text = modelCopy(locale)
  const outOfRange: Outcome = { kind: "error", field: "amount", message: text.outOfRange }
  if (form.amount.trim() === "") return { kind: "error", field: "amount", message: text.empty }
  const amount = parseAmount(form.amount)
  if (!amount) return { kind: "error", field: "amount", message: text.amount }
  const other = form.other.trim() === "" ? money(0, "AUD") : parseAmount(form.other)
  if (!other) return { kind: "error", field: "other", message: text.other }
  const hours = parseHours(form.hoursPerWeek)
  if (form.period === "hour" && hours === undefined)
    return { kind: "error", field: "hours", message: text.hours }
  const days = parseDays(form.daysPerWeek)
  if (form.period === "day" && days === undefined)
    return { kind: "error", field: "days", message: text.days }
  try {
    const result = takeHomePay({
      financialYear: form.fy,
      amount,
      period: form.period,
      hoursPerWeek: hours ?? 38,
      daysPerWeek: days ?? 5,
      includesSuper: form.superIncluded,
      residency: form.residency,
      hasStudyLoan: form.help,
      otherAnnualIncome: other,
      applyStandardDeduction: form.standardDeduction && form.residency === "resident",
      applyMedicareLevy: form.medicare,
    })
    // The engine range-checks the typed amount only; an hourly or daily rate can annualise far above it.
    if (result.annual.gross.amount > MAX_AMOUNT * 100) return outOfRange
    return { kind: "ok", result }
  } catch (error) {
    // The engine range-checks the annualised amount (for example 100 hours at a huge rate).
    if (error instanceof RangeError) return outOfRange
    throw error
  }
}

/** The prerendered one-line answer; the page copy and the test both build it from the engine. */
export function answerSentence(
  form: PayForm,
  formatAud: (value: Money) => string,
  locale: Locale = "en",
): string {
  const outcome = evaluate(form)
  if (outcome.kind !== "ok") throw new Error("answerSentence needs a valid form")
  const { annual, perPeriod } = outcome.result
  const whole = (v: Money) => formatAud(v).replace(/\.00$/, "")
  const fy = form.fy.replace("-", "–")
  return modelCopy(locale).answer({
    salary: whole(annual.gross),
    fortnight: formatAud(perPeriod.fortnight.takeHome),
    fy,
    tax: whole(annual.incomeTax),
    levy: whole(annual.medicareLevy),
  })
}

/* ---------- T1039: helpers behind the charts, the pay-rise slider and the keyboard shortcuts ---------- */

/** Up/Down step on the amount, in cents, per pay period (Shift x10, Alt x0.1). */
export const AMOUNT_STEP_CENTS: Record<PayPeriod, number> = {
  year: 100_000,
  month: 10_000,
  fortnight: 1_000,
  week: 1_000,
  day: 1_000,
  hour: 100,
}

/** Step size in cents for the modifier keys held: Shift x10, Alt x0.1 (Shift wins when both are held). */
export function amountStepCents(
  period: PayPeriod,
  mods: { shift?: boolean; alt?: boolean } = {},
): number {
  const base = AMOUNT_STEP_CENTS[period]
  if (mods.shift) return base * 10
  if (mods.alt) return base / 10
  return base
}

/** The amount after one Up (+1) or Down (-1) press, clamped to 0..MAX_AMOUNT; undefined when the field is invalid. */
export function stepAmount(
  text: string,
  period: PayPeriod,
  direction: 1 | -1,
  mods: { shift?: boolean; alt?: boolean } = {},
): string | undefined {
  const current = text.trim() === "" ? money(0, "AUD") : parseAmount(text)
  if (!current) return undefined
  const next = Math.min(
    MAX_AMOUNT * 100,
    Math.max(0, current.amount + direction * amountStepCents(period, mods)),
  )
  return toDecimalString(money(next, "AUD")).replace(/\.00$/, "")
}

export type SplitKey = "takeHome" | "tax" | "medicare" | "help"
export interface SplitPart {
  key: SplitKey
  amount: Money
  /** Share of pay before tax in basis points; the four shares sum to exactly 10,000 (largest remainder). */
  shareBp: number
}

/** Where pay before tax goes. Shares are display only; the amounts are the engine's cents. */
export function payShares(p: PeriodBreakdown): SplitPart[] {
  const parts: [SplitKey, Money][] = [
    ["takeHome", p.takeHome],
    ["tax", p.incomeTaxAfterLito],
    ["medicare", p.medicareLevy],
    ["help", p.studyLoanRepayment],
  ]
  const gross = p.gross.amount
  if (gross <= 0) return parts.map(([key, amount]) => ({ key, amount, shareBp: 0 }))
  const raw = parts.map(([, v]) => Math.max(0, v.amount) * 10_000)
  const floors = raw.map((r) => Math.floor(r / gross))
  let left = 10_000 - floors.reduce((a, b) => a + b, 0)
  const order = raw
    .map((r, k) => ({ k, rest: r % gross }))
    .toSorted((a, b) => b.rest - a.rest || a.k - b.k)
  for (const { k } of order) {
    if (left <= 0) break
    floors[k] = (floors[k] ?? 0) + 1
    left -= 1
  }
  return parts.map(([key, amount], k) => ({ key, amount, shareBp: floors[k] ?? 0 }))
}

export interface BracketPosition {
  index: number
  rateBp: number
  /** Start of the band, whole dollars ("over A$x"). */
  from: number
  /** Start of the next band, or undefined in the top band. */
  nextFrom?: number
  /** Dollars of taxable income left before the next band starts. */
  toNext?: number
}

/** The band a taxable income falls in, with the same "over A$x" rule as the engine. */
export function bracketPosition(brackets: readonly Bracket[], taxable: number): BracketPosition {
  let index = 0
  brackets.forEach((b, k) => {
    if (taxable > b.from) index = k
  })
  const band = brackets[index]!
  const next = brackets[index + 1]
  return {
    index,
    rateBp: band.rateBp,
    from: band.from,
    ...(next ? { nextFrom: next.from, toNext: next.from - taxable } : {}),
  }
}

export interface TaxCut {
  previousFy: string
  /** Income tax after offsets plus Medicare levy, for the year. */
  previous: Money
  current: Money
  saving: Money
  /** Part of the saving from the standard deduction (0 when it is off or the year has none). */
  deductionEffect: Money
  /** The rest: rate and threshold changes. */
  rateEffect: Money
  /** Rates of this residency's table that changed between the two years, e.g. 16% -> 15%. */
  rateChanges: { fromBp: number; toBp: number }[]
}

const taxAndLevy = (r: TakeHomeResult) =>
  add(subtract(r.annual.incomeTax, r.annual.lito), r.annual.medicareLevy)

/** Same inputs in the year before; undefined for the oldest year in the rule table or an invalid form. */
export function taxCut(form: PayForm): TaxCut | undefined {
  const k = INCOME_TAX_YEARS.findIndex((y) => y.financialYear === form.fy)
  const prevYear = k > 0 ? INCOME_TAX_YEARS[k - 1] : undefined
  if (!prevYear) return undefined
  const now = evaluate(form)
  const before = evaluate({ ...form, fy: prevYear.financialYear })
  const nowNoSd = evaluate({ ...form, standardDeduction: false })
  if (now.kind !== "ok" || before.kind !== "ok" || nowNoSd.kind !== "ok") return undefined
  const current = taxAndLevy(now.result)
  const previous = taxAndLevy(before.result)
  const saving = subtract(previous, current)
  const deductionEffect = subtract(taxAndLevy(nowNoSd.result), current)
  // Same table as the engine uses for this residency (a foreign resident's rates did not change with the resident cut).
  const curBrackets = bracketTable(INCOME_TAX_YEARS[k]!, form.residency).brackets
  const rateChanges = bracketTable(prevYear, form.residency).brackets.flatMap((b, n) => {
    const to = curBrackets[n]?.rateBp
    return to !== undefined && to !== b.rateBp ? [{ fromBp: b.rateBp, toBp: to }] : []
  })
  return {
    previousFy: prevYear.financialYear,
    previous,
    current,
    saving,
    deductionEffect,
    rateEffect: subtract(saving, deductionEffect),
    rateChanges,
  }
}

export interface PayRise {
  /** Extra take-home a year and in the shown period. */
  annual: Money
  perShown: Money
  takeHomePerShown: Money
  /** Whole cents kept of each extra dollar. */
  keptCents: number
}

/** The bracket table the engine uses for a residency. */
export function bracketTable(year: IncomeTaxYear, residency: Residency) {
  return residency === "resident"
    ? year.resident
    : residency === "foreign"
      ? year.foreignResident
      : year.workingHoliday
}

/** Largest yearly rise (whole dollars) that keeps pay before tax within MAX_AMOUNT. */
export function riseCapDollars(result: TakeHomeResult): number {
  return Math.max(0, Math.floor((MAX_AMOUNT * 100 - result.annual.gross.amount) / 100))
}

/** Take-home after a yearly rise of `riseDollars` on top of this job's pay (super not included); the rise is
 * clamped so pay before tax never passes MAX_AMOUNT. */
export function payRise(form: PayForm, result: TakeHomeResult, requested: number): PayRise {
  const riseDollars = Math.min(requested, riseCapDollars(result))
  const zero = money(0, "AUD")
  if (riseDollars <= 0)
    return {
      annual: zero,
      perShown: zero,
      takeHomePerShown: result.perPeriod[form.show].takeHome,
      keptCents: 0,
    }
  const gross = add(result.annual.gross, money(riseDollars * 100, "AUD"))
  const raised = evaluate({
    ...form,
    amount: toDecimalString(gross),
    period: "year",
    superIncluded: false,
  })
  if (raised.kind !== "ok")
    return {
      annual: zero,
      perShown: zero,
      takeHomePerShown: result.perPeriod[form.show].takeHome,
      keptCents: 0,
    }
  const annual = subtract(raised.result.annual.takeHome, result.annual.takeHome)
  return {
    annual,
    perShown: subtract(
      raised.result.perPeriod[form.show].takeHome,
      result.perPeriod[form.show].takeHome,
    ),
    takeHomePerShown: raised.result.perPeriod[form.show].takeHome,
    keptCents: Math.round(annual.amount / riseDollars),
  }
}

const dollarsText = (n: number) => `A$${n.toLocaleString("en-AU")}`

/** The sentence under the bracket ladder (its text equivalent). Taxable income in whole dollars. */
export function bracketCaption(
  brackets: readonly Bracket[],
  taxable: number,
  locale: Locale = "en",
): string {
  const text = modelCopy(locale)
  const pos = bracketPosition(brackets, taxable)
  const now =
    pos.rateBp === 0
      ? text.bracketNil(dollarsText(taxable))
      : text.bracketRate(dollarsText(taxable), pos.rateBp / 100)
  const next = brackets[pos.index + 1]
  if (!next || pos.toNext === undefined) return `${now}${text.bracketTop}`
  return `${now}${text.bracketNext({ rate: next.rateBp / 100, from: dollarsText(next.from + 1), toNext: dollarsText(pos.toNext + 1) })}`
}

/** The prerendered one-line answer of the HELP and Medicare pages, from the engine at that page's defaults. */
export function variantAnswer(
  variant: "help" | "medicare",
  formatAud: (value: Money) => string,
  locale: Locale = "en",
): string {
  const text = modelCopy(locale)
  const form = VARIANT_DEFAULTS[variant]
  const outcome = evaluate(form)
  if (outcome.kind !== "ok") throw new Error("variantAnswer needs a valid form")
  const { annual, perPeriod } = outcome.result
  const whole = (v: Money) => formatAud(v).replace(/\.00$/, "")
  const fy = form.fy.replace("-", "–")
  const salary = whole(annual.gross)
  if (variant === "help")
    return text.helpAnswer({
      salary,
      annual: whole(annual.studyLoanRepayment),
      fortnight: formatAud(perPeriod.fortnight.studyLoanRepayment),
      fy,
    })
  return text.medicareAnswer({
    salary,
    annual: whole(annual.medicareLevy),
    fortnight: formatAud(perPeriod.fortnight.medicareLevy),
    fy,
    rate: incomeTaxYear(form.fy).medicare.rateBp / 100,
    taxable: whole(annual.taxableIncome),
  })
}

/** "A$69,529 – A$129,717" for band `k` of a table whose bands start at `from` (the first band starts at A$0). */
export function bandRange(
  bands: readonly { from: number }[],
  k: number,
  locale: Locale = "en",
): string {
  const text = modelCopy(locale)
  const b = bands[k]!
  const next = bands[k + 1]
  const from = k === 0 ? "A$0" : dollarsText(b.from + 1)
  return next
    ? text.rangeBetween(from, dollarsText(next.from))
    : text.rangeOpen(dollarsText(b.from + 1))
}

/** "A$69,529 – A$129,717" for band `k` of a HELP table (the first band starts at A$0). */
export function studyLoanRange(
  bands: readonly StudyLoanBand[],
  k: number,
  locale: Locale = "en",
): string {
  return bandRange(bands, k, locale)
}

/** The repayment rule of one HELP band, in the ATO's words ("15c for each A$1 over A$69,528"). */
export function studyLoanRule(b: StudyLoanBand, locale: Locale = "en"): string {
  const text = modelCopy(locale)
  if (b.rateBp === 0) return text.ruleNil
  if (b.ofWholeIncome) return text.ruleWhole(b.rateBp / 100)
  return text.ruleOver({
    base: b.baseDollars > 0 ? dollarsText(b.baseDollars) : undefined,
    cents: b.rateBp / 100,
    from: dollarsText(b.from),
  })
}
